<script setup lang="ts">
import { useCurrentUser } from "@/api/current-user";
import { useScopeStations } from "@/api/station-query";
import { Station, UserScope } from "@soft-db/monitoring-api-client";
import {
	ColumnSelection,
	DataTableContent,
	DataTablePagination,
	DataTableRoot,
	DataTableSearch,
	Icon,
	makeColumnHelper,
	RowSelectionState,
} from "@soft-db/ui";
import AppDialog from "../common/AppDialog.vue";
import ColumnName from "../common/ColumnName.vue";
import { icPlaceOutline } from "../icons.ts";
import SiteLinkedBadge from "../sites/SiteLinkedBadge.vue";
import NavBarStatus from "../workspace/NavBarStatus.vue";

const props = defineProps<{
	scope?: UserScope | null;

	/**
	 * A list of stations to hide from the table
	 */
	ignore?: string[];
}>();

const emit = defineEmits<{
	select: [stations: Station[]];
}>();

const { t } = useI18n();

const dialog = defineModel<boolean>("dialog");

const { currentUser } = useCurrentUser();

const { stations: scopeStations } = useScopeStations(
	() => props.scope || currentUser.value?.scope || { type: "none", id: "" }
);

const selection = ref<RowSelectionState>({});

const stations = computed(() => {
	const toIgnore = new Set(props.ignore || []);

	return scopeStations.value?.filter((site) => !toIgnore.has(site.id)) || [];
});

function toggleSelection(station: Station) {
	if (station.id in selection.value) {
		delete selection.value[station.id];
	} else {
		selection.value[station.id] = true;
	}
}

const hasSelection = computed(() => {
	return Object.values(selection.value).some(Boolean);
});

function save() {
	const selectedStations = stations.value.filter((s) => selection.value[s.id] === true);
	emit("select", selectedStations);

	close();
}

function close() {
	selection.value = {};
	dialog.value = false;
}

const { Column } = makeColumnHelper<Station>();
</script>

<template>
	<AppDialog
		v-model="dialog"
		class="w-[800px]"
		:actionText="t('common.save')"
		:actionDisabled="!hasSelection"
		@click:action="save"
		@close="close"
	>
		<template #title>
			<Icon :d="icPlaceOutline" />
			<span>{{ t("space.addStation") }}</span>
		</template>
		<template #content>
			<DataTableRoot v-model:selection="selection" :data="stations || []">
				<div class="flex flex-col gap-2">
					<DataTableSearch />
					<!-- @vue-generic {Station} -->
					<DataTableContent @click:row="(row) => toggleSelection(row.row.original)">
						<ColumnSelection selectionMode="multiple" />
						<Column
							v-slot="{ row: { original: station } }"
							field="name"
							:header="t('common.name')"
							enableSorting
							enableGlobalFilter
						>
							<ColumnName :name="station.name" :description="station.description" />
						</Column>

						<Column
							field="serialNumber"
							:header="t('station.serialNumber')"
							enableSorting
							enableGlobalFilter
						/>

						<Column
							id="site"
							v-slot="{ row: { original: station } }"
							:fieldFn="(d) => d.site?.name || ''"
							:header="t('object.project', 1)"
							enableSorting
							enableGlobalFilter
						>
							<SiteLinkedBadge :site="station.site" />
						</Column>

						<Column v-slot="{ row: { original: station } }" shrink>
							<NavBarStatus class="mr-4" sourceType="station" :sourceId="station.id" />
						</Column>
					</DataTableContent>
					<div class="flex md:justify-end">
						<DataTablePagination :defaultPageSize="7" />
					</div>
				</div>
			</DataTableRoot>
		</template>
	</AppDialog>
</template>
